import React, { useEffect, useRef, useLayoutEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import ResponseDocument, { splitIntoSections } from "./ResponseDocument";
import ResearchSkeleton from "./ResearchSkeleton";
import "./Dashboard.css";

/**
 * ResponseContent - Displays formatted HTML response with loading overlay
 * @param {boolean} plain - Render the response as one continuous block of text
 *   at its natural height: no collapsible outline, no fixed-height scrollbox,
 *   no auto-scroll. Used by the consumer /ask page; the dashboard keeps the
 *   default card presentation.
 * @param {string} htmlContent - The HTML content to display
 * @param {boolean} isLoading - Whether response is currently loading
 * @param {string} modelName - Name of the model (for loading message)
 * @param {boolean} hasError - Whether there's an error
 * @param {string} errorMessage - Error message to display
 * @param {Object} contentRef - Ref to attach to content div
 * @param {string} className - Additional CSS classes
 */
export default function ResponseContent({
  htmlContent = "",
  isLoading = false,
  modelName = "",
  hasError = false,
  errorMessage = "",
  contentRef = null,
  className = "",
  plain = false
}) {
  const containerRef = useRef(null);
  const innerContentRef = useRef(null);
  const userScrolledRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const [showFullContent, setShowFullContent] = useState(false);
  const isSmallViewport =
    typeof window !== "undefined" ? window.innerWidth <= 900 : false;
  const MAX_MOBILE_HTML_LENGTH = 120000;
  const shouldTruncateOnMobile =
    isSmallViewport && !isLoading && htmlContent.length > MAX_MOBILE_HTML_LENGTH;
  const renderedHtmlContent = useMemo(() => {
    if (shouldTruncateOnMobile && !showFullContent) {
      return htmlContent.slice(0, MAX_MOBILE_HTML_LENGTH);
    }
    return htmlContent;
  }, [htmlContent, shouldTruncateOnMobile, showFullContent]);

  useEffect(() => {
    if (!shouldTruncateOnMobile) {
      setShowFullContent(false);
    }
  }, [shouldTruncateOnMobile]);

  // Sectioned document view. Skipped entirely in `plain` mode, and
  // deliberately NOT built while `isLoading` is true:
  // during streaming we keep the original single-blob markup so the auto-scroll
  // and MutationObserver logic below (and in Dashboard.js) is unaffected.
  const parsedDocument = useMemo(() => {
    if (plain) return null;
    if (isLoading || !renderedHtmlContent || !renderedHtmlContent.trim()) return null;
    return splitIntoSections(renderedHtmlContent);
  }, [plain, isLoading, renderedHtmlContent]);

  // Helper to check if user is near bottom
  const isNearBottom = (element, threshold = 100) => {
    if (!element) return true;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= threshold;
  };

  // Combine refs
  const setRefs = (node) => {
    containerRef.current = node;
    if (contentRef) {
      if (typeof contentRef === 'function') {
        contentRef(node);
      } else {
        contentRef.current = node;
      }
    }
  };

  // Track user scroll events
  useEffect(() => {
    if (!containerRef.current) return;

    const scrollableEl = containerRef.current;
    let scrollTimeout;

    const handleScroll = () => {
      if (!scrollableEl) return;

      const currentScrollTop = scrollableEl.scrollTop;
      const scrollHeight = scrollableEl.scrollHeight;
      const clientHeight = scrollableEl.clientHeight;
      const isAtBottom = scrollHeight - currentScrollTop - clientHeight <= 100;

      // If user scrolled up (not at bottom), mark as user-interacted
      if (!isAtBottom && currentScrollTop < lastScrollTopRef.current) {
        userScrolledRef.current = true;
      } else if (isAtBottom) {
        // If user scrolls back to bottom, reset the flag
        userScrolledRef.current = false;
      }

      lastScrollTopRef.current = currentScrollTop;

      // Reset user scroll flag after a delay if they're at bottom
      clearTimeout(scrollTimeout);
      if (isAtBottom) {
        scrollTimeout = setTimeout(() => {
          userScrolledRef.current = false;
        }, 1000);
      }
    };

    scrollableEl.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollableEl.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Auto-scroll to bottom when content changes
  useLayoutEffect(() => {
    if (plain || !containerRef.current) return;

    const scrollableEl = containerRef.current;

    // Scroll to bottom immediately after DOM update (only if user hasn't scrolled up)
    const scrollToBottom = () => {
      if (scrollableEl && !userScrolledRef.current) {
        scrollableEl.scrollTop = scrollableEl.scrollHeight;
        lastScrollTopRef.current = scrollableEl.scrollTop;
      }
    };

    // Only auto-scroll if we're loading (streaming) or content exists, and user hasn't scrolled up
    if ((isLoading || htmlContent) && !userScrolledRef.current) {
      scrollToBottom();

      // Also scroll after DOM updates with multiple timing strategies
      requestAnimationFrame(() => {
        scrollToBottom();
        setTimeout(() => {
          scrollToBottom();
        }, 0);
      });
    }
  }, [renderedHtmlContent, isLoading, plain]);

  // Set up MutationObserver to watch for content changes during streaming.
  // Keep it lightweight to avoid mobile browser jank on long responses.
  useEffect(() => {
    if (plain || !containerRef.current || !isLoading) return;

    const scrollableEl = containerRef.current;
    const innerDiv = scrollableEl.querySelector('div');
    const targetToObserve = innerDiv || scrollableEl;

    const scrollToBottom = () => {
      if (scrollableEl && !userScrolledRef.current && isNearBottom(scrollableEl)) {
        scrollableEl.scrollTop = scrollableEl.scrollHeight;
        lastScrollTopRef.current = scrollableEl.scrollTop;
      }
    };

    // Create observer to watch for content changes
    const observer = new MutationObserver(() => {
      // Only auto-scroll when content changes during loading if user is near bottom
      if (isLoading && !userScrolledRef.current && isNearBottom(scrollableEl)) {
        // Scroll immediately when content changes
        scrollToBottom();
        requestAnimationFrame(() => {
          scrollToBottom();
          // Double-check after microtask to ensure we catch all updates
          setTimeout(() => {
            scrollToBottom();
          }, 0);
        });
      }
    });

    // Observe the inner content div (where dangerouslySetInnerHTML updates)
    if (targetToObserve) {
      observer.observe(targetToObserve, {
        childList: true,
        subtree: true
      });
    }

    // Also observe the container itself for structural changes
    if (targetToObserve !== scrollableEl) {
      observer.observe(scrollableEl, {
        childList: true
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [isLoading, plain]);

  if (hasError) {
    return (
      <div className="rs-error" role="alert">
        <span className="rs-error-icon">
          <AlertTriangle size={17} />
        </span>
        <div className="rs-error-body">
          <p className="rs-error-title">
            {modelName ? `${modelName} could not complete this analysis` : "Analysis failed"}
          </p>
          <p className="rs-error-message">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`response-html ${plain ? "response-html--plain" : ""} ${className}`}
      style={{ position: 'relative', minHeight: '200px' }}
      ref={setRefs}
    >
      {parsedDocument ? (
        <div ref={innerContentRef}>
          <ResponseDocument parsed={parsedDocument} modelName={modelName} />
        </div>
      ) : (
        <div ref={innerContentRef} dangerouslySetInnerHTML={{ __html: renderedHtmlContent }} />
      )}
      {shouldTruncateOnMobile && !showFullContent && (
        <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
          <button
            type="button"
            className="comparison-action-btn"
            onClick={() => setShowFullContent(true)}
          >
            Load full response
          </button>
        </div>
      )}
      <AnimatePresence>
        {isLoading && !htmlContent.trim() && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="response-loading-overlay rs-loading-overlay"
          >
            <div className="rs-loading-head">
              <div className="loading-spinner-circle"></div>
              <span className="loading-text">
                {modelName ? `${modelName} is reasoning through the issue…` : "Response generating…"}
              </span>
            </div>
            <ResearchSkeleton modelName={modelName} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

